import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  getDetailedConnections,
  deleteConnection,
  updateConnectionTiers,
  startSearchGroundedEnrich,
  getSearchGroundedStatus,
  getStoredGroundedJobId,
  setStoredGroundedJobId,
} from '../services/connectionsService';
import { DetailedConnection } from '../types/connections';

const TIER1_POPUP_DISMISSED_KEY = 'connections-tier1-popup-dismissed';

/** Inputs/selects: browsers default to light styling; force dark surfaces and readable text */
const formControlClass =
  'border border-gray-300 rounded-lg bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-ts-gold';

const PROFESSIONAL_OPTIONS: { value: '' | number; label: string }[] = [
  { value: '', label: '— (null)' },
  { value: 1, label: 'Executive (1)' },
  { value: 2, label: 'Management (2)' },
  { value: 3, label: 'Individual Contributor (3)' },
];

const COHORT_OPTIONS: { value: '' | number; label: string }[] = [
  { value: '', label: '— (null)' },
  { value: 1, label: 'Two or More (1)' },
  { value: 2, label: 'One (2)' },
];

type EnrichedDateFilter = 'all' | 'today' | 'last-7d' | 'last-30d' | 'missing';

const getConnectionEnrichedMs = (conn: DetailedConnection): number | null => {
  const raw = conn.createdAt || conn.updatedAt;
  if (!raw) return null;
  const ms = new Date(raw).getTime();
  return Number.isNaN(ms) ? null : ms;
};

const formatEnrichedDate = (conn: DetailedConnection): string | null => {
  const ms = getConnectionEnrichedMs(conn);
  if (!ms) return null;
  return new Date(ms).toLocaleString();
};

const ConnectionsListView: React.FC = () => {
  const { user, isAdmin } = useAuth();
  const [connections, setConnections] = useState<DetailedConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTier, setFilterTier] = useState<string>('all');
  const [filterEnrichedDate, setFilterEnrichedDate] = useState<EnrichedDateFilter>('all');
  const [customFrom, setCustomFrom] = useState<string>('');
  const [customTo, setCustomTo] = useState<string>('');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [showTier1Popup, setShowTier1Popup] = useState(() => !localStorage.getItem(TIER1_POPUP_DISMISSED_KEY));

  // Search-grounded enrichment state
  const [groundedJobId, setGroundedJobId] = useState<string | null>(getStoredGroundedJobId());
  const [groundedProgress, setGroundedProgress] = useState(0);
  const [groundedMessage, setGroundedMessage] = useState<string | null>(null);
  const [groundedError, setGroundedError] = useState<string | null>(null);
  const [groundedResult, setGroundedResult] = useState<string | null>(null);
  const [startingGrounded, setStartingGrounded] = useState(false);
  const groundedPollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const groundedConsecutive404s = useRef(0);
  const mountedRef = useRef(true);
  useEffect(() => { return () => { mountedRef.current = false; }; }, []);

  const dismissTier1Popup = () => {
    setShowTier1Popup(false);
    localStorage.setItem(TIER1_POPUP_DISMISSED_KEY, '1');
  };

  const handleStartGroundedEnrich = useCallback(async () => {
    if (!user) return;
    setStartingGrounded(true);
    setGroundedError(null);
    setGroundedResult(null);
    groundedConsecutive404s.current = 0;
    try {
      const token = await user.getIdToken();
      const res = await startSearchGroundedEnrich(token, 50);
      setGroundedJobId(res.jobId);
      setStoredGroundedJobId(res.jobId);
      setGroundedMessage(res.message);
      setGroundedProgress(5);
    } catch (err: any) {
      setGroundedError(err?.message || 'Failed to start search-grounded enrichment');
    } finally {
      setStartingGrounded(false);
    }
  }, [user]);

  // Poll for search-grounded job status
  useEffect(() => {
    if (!groundedJobId || !user) return;
    const poll = async () => {
      try {
        const token = await user.getIdToken();
        const status = await getSearchGroundedStatus(token, groundedJobId);
        setGroundedMessage(status.message ?? 'Processing...');
        setGroundedProgress(status.progress ?? 0);
        if (status.status === 'completed') {
          const r = status.result;
          const cost = r?.estimatedCostUsd != null ? ` Est. cost: $${r.estimatedCostUsd.toFixed(4)}.` : '';
          const dur = r?.enrichDurationSec != null ? ` (${Math.round(r.enrichDurationSec)}s)` : '';
          const tierNote =
            r?.tiersRefreshed === true
              ? ' Professional & cohort tiers were recomputed from the expanded work history and saved.'
              : '';
          setGroundedResult(
            `Enhanced ${r?.enriched ?? 0} connections with search grounding${dur}.` +
            ` Skipped ${r?.skipped_already_grounded ?? 0} already grounded.` +
            ` Total grounded: ${r?.total_grounded ?? 0}/${r?.cap ?? 200}.${tierNote}${cost}`
          );
          setGroundedJobId(null);
          setStoredGroundedJobId(null);
          setGroundedProgress(100);
          loadConnections();
        } else if (status.status === 'failed') {
          setGroundedError(status.error || 'Search-grounded enrichment failed');
          setGroundedJobId(null);
          setStoredGroundedJobId(null);
          setGroundedProgress(0);
        } else {
          groundedConsecutive404s.current = 0;
          groundedPollRef.current = setTimeout(poll, 30000);
        }
      } catch (err: any) {
        const msg = err?.message ?? '';
        if (msg.includes('404') || msg.includes('Job not found')) {
          groundedConsecutive404s.current += 1;
          if (groundedConsecutive404s.current >= 2) {
            setGroundedError('The server restarted and the job was lost. Please try again.');
            setGroundedJobId(null);
            setStoredGroundedJobId(null);
            setGroundedProgress(0);
            return;
          }
        }
        groundedPollRef.current = setTimeout(poll, 30000);
      }
    };
    poll();
    return () => {
      if (groundedPollRef.current) clearTimeout(groundedPollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groundedJobId, user]);

  useEffect(() => {
    loadConnections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadConnections = async () => {
    if (!user) return;
    mountedRef.current = true;

    try {
      setLoading(true);
      setError(null);
      const token = await user.getIdToken();
      const response = await getDetailedConnections(token);
      if (mountedRef.current) setConnections(response.connections);
    } catch (err: any) {
      if (mountedRef.current) setError(err.message || 'Failed to load connections');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConnection = async (conn: DetailedConnection) => {
    const id = conn.id;
    if (!id || !user) return;
    if (!window.confirm(`Remove "${conn.firstName} ${conn.lastName}" from your connections? This only deletes your stored copy.`)) return;
    try {
      setDeletingId(id);
      setError(null);
      const token = await user.getIdToken();
      await deleteConnection(token, id);
      setConnections((prev) => prev.filter((c) => c.id !== id));
    } catch (err: any) {
      setError(err.message || 'Failed to delete connection');
    } finally {
      setDeletingId(null);
    }
  };

  const handleTierChange = async (
    conn: DetailedConnection,
    field: 'professionalTier' | 'cohortAlumniTier',
    rawValue: string
  ) => {
    const id = conn.id;
    if (!id || !user) return;
    const value = rawValue === '' ? null : parseInt(rawValue, 10);
    if (field === 'professionalTier' && value !== null && ![1, 2, 3].includes(value)) return;
    if (field === 'cohortAlumniTier' && value !== null && ![1, 2].includes(value)) return;
    try {
      setSavingId(id);
      setError(null);
      const token = await user.getIdToken();
      await updateConnectionTiers(token, id, { [field]: value });
      setConnections((prev) =>
        prev.map((c) => (c.id === id ? { ...c, [field]: value } : c))
      );
    } catch (err: any) {
      setError(err.message || 'Failed to update tier');
    } finally {
      setSavingId(null);
    }
  };

  const filteredConnections = connections.filter((conn) => {
    const search = searchTerm.toLowerCase();
    const matchesSearch =
      `${conn.firstName} ${conn.lastName}`.toLowerCase().includes(search) ||
      (conn.companysAndPositions || []).some(
        (cp) =>
          cp.company?.toLowerCase().includes(search) || cp.position?.toLowerCase().includes(search)
      );
    
    const matchesTier = filterTier === 'all' ||
      (filterTier === 'professional-1' && conn.professionalTier === 1) ||
      (filterTier === 'professional-2' && conn.professionalTier === 2) ||
      (filterTier === 'professional-3' && conn.professionalTier === 3) ||
      (filterTier === 'cohort-1' && conn.cohortAlumniTier === 1) ||
      (filterTier === 'cohort-2' && conn.cohortAlumniTier === 2) ||
      (filterTier === 'search-grounded' && conn.searchGrounded === true) ||
      (filterTier === 'not-grounded' && !conn.searchGrounded);

    const enrichedMs = getConnectionEnrichedMs(conn);
    const now = Date.now();
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    let matchesEnrichedDate = true;
    if (filterEnrichedDate === 'today') {
      matchesEnrichedDate = enrichedMs !== null && enrichedMs >= startOfToday.getTime();
    } else if (filterEnrichedDate === 'last-7d') {
      matchesEnrichedDate = enrichedMs !== null && enrichedMs >= now - 7 * 24 * 60 * 60 * 1000;
    } else if (filterEnrichedDate === 'last-30d') {
      matchesEnrichedDate = enrichedMs !== null && enrichedMs >= now - 30 * 24 * 60 * 60 * 1000;
    } else if (filterEnrichedDate === 'missing') {
      matchesEnrichedDate = enrichedMs === null;
    }

    if (customFrom) {
      const fromMs = new Date(`${customFrom}T00:00:00`).getTime();
      if (!Number.isNaN(fromMs)) {
        matchesEnrichedDate = matchesEnrichedDate && enrichedMs !== null && enrichedMs >= fromMs;
      }
    }
    if (customTo) {
      const toMs = new Date(`${customTo}T23:59:59`).getTime();
      if (!Number.isNaN(toMs)) {
        matchesEnrichedDate = matchesEnrichedDate && enrichedMs !== null && enrichedMs <= toMs;
      }
    }

    return matchesSearch && matchesTier && matchesEnrichedDate;
  });

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ts-gold mx-auto mb-4"></div>
        <p className="text-gray-600">Loading connections...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {showTier1Popup && (
        <div className="bg-ts-cream border border-ts-border rounded-lg px-4 py-3 flex items-start justify-between gap-3">
          <p className="text-sm text-ts-gold-deep">
            <strong>Tip:</strong> Assign <strong>Executive (Tier 1)</strong> based on your own knowledge—e.g. close mentors, C-suite contacts, or key decision-makers you know well. This boosts their connection score for companies to target.
          </p>
          <button
            type="button"
            onClick={dismissTier1Popup}
            aria-label="Dismiss"
            className="shrink-0 text-ts-gold hover:text-ts-gold-deep focus:outline-none"
          >
            ✕
          </button>
        </div>
      )}
      <div className="flex flex-wrap justify-between items-center gap-3">
        <h1 className="text-3xl font-bold text-gray-900">My Connections</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={handleStartGroundedEnrich}
            disabled={!!groundedJobId || startingGrounded || connections.length === 0}
            title="Re-enrich up to 50 top contacts with Google Search (Tier 1 first, then 2/3): prior roles, company scores, and refreshed tiers. Skips only already search-grounded. Cap 200 per user (~$3)."
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {startingGrounded ? 'Starting...' : groundedJobId ? 'Enriching...' : 'Search-Grounded Enrich'}
          </button>
          <button
            onClick={loadConnections}
            className="px-4 py-2 bg-ts-navy text-white rounded-lg hover:bg-ts-charcoal focus:outline-none focus:ring-2 focus:ring-ts-gold transition-colors"
          >
            Refresh list
          </button>
        </div>
      </div>

      {/* Search-Grounded Enrichment Progress */}
      {groundedJobId && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg px-4 py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-purple-500 border-t-transparent" />
              <div>
                <p className="font-medium text-purple-900">Search-Grounded Enrichment</p>
                <p className="text-sm text-purple-700">{groundedMessage ?? 'Processing...'}</p>
              </div>
            </div>
            <p className="text-sm text-purple-700 font-medium">{Math.round(groundedProgress)}%</p>
          </div>
          <div className="mt-2 w-full bg-purple-100 rounded-full h-2">
            <div
              className="bg-purple-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${Math.max(5, groundedProgress)}%` }}
            />
          </div>
          <p className="text-xs text-purple-600 mt-2">
            Uses Google Search for prior roles; professional & cohort tiers are recomputed from the full history; company scores refresh after. Prioritizes Tier 1, then 2 and 3; skips only already grounded. Cap: 200 per user (~$3).
          </p>
        </div>
      )}
      {groundedResult && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex justify-between items-start">
          <p className="text-sm">{groundedResult}</p>
          <button onClick={() => setGroundedResult(null)} className="ml-2 text-green-600 hover:text-green-800 shrink-0">✕</button>
        </div>
      )}
      {groundedError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex justify-between items-start">
          <p className="text-sm">{groundedError}</p>
          <button onClick={() => setGroundedError(null)} className="ml-2 text-red-600 hover:text-red-800 shrink-0">✕</button>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, company, or position..."
              className={`w-full px-3 py-2 ${formControlClass}`}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filter by Tier
            </label>
            <select
              value={filterTier}
              onChange={(e) => setFilterTier(e.target.value)}
              className={`w-full min-w-[14rem] pr-8 px-3 py-2 ${formControlClass}`}
            >
              <option value="all">All Connections</option>
              <option value="professional-1">Executive (Tier 1)</option>
              <option value="professional-2">Management Tier</option>
              <option value="professional-3">Individual Contributor</option>
              <option value="cohort-1">Two or More (1)</option>
              <option value="cohort-2">One (2)</option>
              <option value="search-grounded">Search Grounded</option>
              <option value="not-grounded">Not Search Grounded</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Enriched Date
            </label>
            <select
              value={filterEnrichedDate}
              onChange={(e) => setFilterEnrichedDate(e.target.value as EnrichedDateFilter)}
              className={`w-full min-w-[14rem] pr-8 px-3 py-2 ${formControlClass}`}
            >
              <option value="all">Any time</option>
              <option value="today">Today</option>
              <option value="last-7d">Last 7 days</option>
              <option value="last-30d">Last 30 days</option>
              <option value="missing">No enriched timestamp</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Enriched From
            </label>
            <input
              type="date"
              value={customFrom}
              onChange={(e) => setCustomFrom(e.target.value)}
              className={`w-full px-3 py-2 ${formControlClass}`}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Enriched To
            </label>
            <input
              type="date"
              value={customTo}
              onChange={(e) => setCustomTo(e.target.value)}
              className={`w-full px-3 py-2 ${formControlClass}`}
            />
          </div>
        </div>
        <p className="mt-2 text-sm text-gray-500">
          Showing {filteredConnections.length} of {connections.length} connections
        </p>
      </div>

      {/* Connections List */}
      {filteredConnections.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-8 text-center">
          <p className="text-gray-600">No connections found. Upload a CSV file to get started.</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredConnections.map((conn, index) => {
            const enrichedLabel = formatEnrichedDate(conn);
            return (
            <div
              key={conn.id ?? index}
              className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex flex-col lg:flex-row lg:items-start lg:gap-4">

                {/* Left: connection info */}
                <div className="flex-1">
                  <div className="flex justify-between items-start">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      {conn.firstName} {conn.lastName}
                    </h3>
                    {/* Delete button — mobile only; desktop version is in the right column */}
                    {conn.id && isAdmin && (
                      <button
                        type="button"
                        onClick={() => handleDeleteConnection(conn)}
                        disabled={deletingId === conn.id}
                        className="lg:hidden ml-3 px-3 py-1.5 text-sm border border-red-300 text-red-700 rounded-lg hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
                        title="Remove this connection from your stored data"
                      >
                        {deletingId === conn.id ? 'Removing...' : 'Remove'}
                      </button>
                    )}
                  </div>

                  {conn.companysAndPositions && conn.companysAndPositions.length > 0 && (
                    <div className="space-y-1">
                      <p className="text-gray-700">
                        {conn.companysAndPositions[0].position ? (
                          <>
                            <span className="font-medium">{conn.companysAndPositions[0].position}</span> at{' '}
                            <span className="font-medium">{conn.companysAndPositions[0].company}</span>
                          </>
                        ) : (
                          <span className="font-medium">{conn.companysAndPositions[0].company}</span>
                        )}
                      </p>
                      {conn.companysAndPositions.length > 1 && (
                        <p className="text-sm text-gray-500">
                          Previously: {conn.companysAndPositions.slice(1).map(cp => {
                            const suffix = cp.timePeriod || cp.months ? ` (${cp.timePeriod || `${cp.months} mo`})` : '';
                            return cp.position ? `${cp.position} at ${cp.company}${suffix}` : `${cp.company}${suffix}`;
                          }).join(', ')}
                        </p>
                      )}
                    </div>
                  )}

                  {conn.emailAddress && (
                    <p className="text-sm text-gray-600 mt-2">
                      📧 {conn.emailAddress}
                    </p>
                  )}

                  {conn.linkedInURL && (
                    <a
                      href={conn.linkedInURL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-ts-gold hover:text-ts-gold-deep text-sm mt-2 inline-block"
                    >
                      View LinkedIn Profile →
                    </a>
                  )}
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    {conn.searchGrounded && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                        Search Grounded
                      </span>
                    )}
                    {enrichedLabel && (
                      <span className="text-xs text-gray-500">
                        Enriched: {enrichedLabel}
                      </span>
                    )}
                  </div>

                  {/* Tiers — mobile only (desktop shows in right column) */}
                  {conn.id && (
                    <div className="lg:hidden mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-xs font-medium text-gray-600 mb-3 uppercase tracking-wide">Connection Quality</p>
                      <div className="flex flex-wrap items-center gap-4">
                        <div className="flex items-center gap-2">
                          <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Professional</label>
                          <select
                            value={conn.professionalTier === null || conn.professionalTier === undefined ? '' : String(conn.professionalTier)}
                            onChange={(e) => handleTierChange(conn, 'professionalTier', e.target.value)}
                            disabled={savingId === conn.id}
                            className={`min-w-[11rem] pr-8 px-3 py-1.5 text-sm ${formControlClass} disabled:opacity-50`}
                          >
                            {PROFESSIONAL_OPTIONS.map((opt) => (
                              <option key={String(opt.value)} value={opt.value === '' ? '' : String(opt.value)}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="flex items-center gap-2">
                          <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Mutual Experiences/Education</label>
                          <select
                            value={conn.cohortAlumniTier === null || conn.cohortAlumniTier === undefined ? '' : String(conn.cohortAlumniTier)}
                            onChange={(e) => handleTierChange(conn, 'cohortAlumniTier', e.target.value)}
                            disabled={savingId === conn.id}
                            className={`min-w-[11rem] pr-8 px-3 py-1.5 text-sm ${formControlClass} disabled:opacity-50`}
                          >
                            {COHORT_OPTIONS.map((opt) => (
                              <option key={String(opt.value)} value={opt.value === '' ? '' : String(opt.value)}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        {savingId === conn.id && (
                          <span className="text-sm text-gray-500">Saving…</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Right: tiers + delete (desktop only) */}
                {conn.id && (
                  <div className="hidden lg:flex lg:flex-col lg:w-1/2 shrink-0 gap-3">
                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-xs font-medium text-gray-600 mb-3 uppercase tracking-wide">Connection Quality</p>
                      <div className="flex flex-col gap-3">
                        <div className="flex items-center gap-3">
                          <label className="text-sm font-medium text-gray-700 whitespace-nowrap w-36 shrink-0">Professional</label>
                          <select
                            value={conn.professionalTier === null || conn.professionalTier === undefined ? '' : String(conn.professionalTier)}
                            onChange={(e) => handleTierChange(conn, 'professionalTier', e.target.value)}
                            disabled={savingId === conn.id}
                            className={`flex-1 pr-8 px-3 py-1.5 text-sm ${formControlClass} disabled:opacity-50`}
                          >
                            {PROFESSIONAL_OPTIONS.map((opt) => (
                              <option key={String(opt.value)} value={opt.value === '' ? '' : String(opt.value)}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="flex items-center gap-3">
                          <label className="text-sm font-medium text-gray-700 whitespace-nowrap w-36 shrink-0">Mutual Exp/Edu</label>
                          <select
                            value={conn.cohortAlumniTier === null || conn.cohortAlumniTier === undefined ? '' : String(conn.cohortAlumniTier)}
                            onChange={(e) => handleTierChange(conn, 'cohortAlumniTier', e.target.value)}
                            disabled={savingId === conn.id}
                            className={`flex-1 pr-8 px-3 py-1.5 text-sm ${formControlClass} disabled:opacity-50`}
                          >
                            {COHORT_OPTIONS.map((opt) => (
                              <option key={String(opt.value)} value={opt.value === '' ? '' : String(opt.value)}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        {savingId === conn.id && (
                          <span className="text-sm text-gray-500">Saving…</span>
                        )}
                      </div>
                    </div>
                    {isAdmin && (
                      <button
                        type="button"
                        onClick={() => handleDeleteConnection(conn)}
                        disabled={deletingId === conn.id}
                        className="px-3 py-1.5 text-sm border border-red-300 text-red-700 rounded-lg hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 self-start"
                        title="Remove this connection from your stored data"
                      >
                        {deletingId === conn.id ? 'Removing...' : 'Remove'}
                      </button>
                    )}
                  </div>
                )}

              </div>

              {conn.education && conn.education.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-sm font-medium text-gray-700 mb-1">Education:</p>
                  <div className="flex flex-wrap gap-2">
                    {conn.education.map((edu: { schoolName: string; degree?: string; concentration?: string }, eduIndex: number) => (
                      <span key={eduIndex} className="text-sm text-gray-600">
                        {edu.schoolName}
                        {edu.degree && ` - ${edu.degree}`}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ConnectionsListView;


